import { useState, useEffect, useRef } from "react";

export const SECTION_REVEAL_OPTIONS = {
  threshold: 0.06,
  rootMargin: "0px 0px 10% 0px",
};

const MOBILE_REVEAL_MQ = "(max-width: 900px)";

function prefersNoScrollReveal() {
  if (typeof window === "undefined") return false;
  return window.matchMedia(MOBILE_REVEAL_MQ).matches;
}

export function useIntersectionObserver(
  ref,
  { threshold, root, rootMargin } = SECTION_REVEAL_OPTIONS
) {
  const [inView, setInView] = useState(() => prefersNoScrollReveal());
  const triggeredRef = useRef(prefersNoScrollReveal());

  useEffect(() => {
    if (triggeredRef.current) return;

    const el = ref.current;
    if (!el) return;

    if (prefersNoScrollReveal()) {
      triggeredRef.current = true;
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !triggeredRef.current) {
          triggeredRef.current = true;
          observer.disconnect();
          window.requestAnimationFrame(() => setInView(true));
        }
      },
      {
        threshold: threshold ?? SECTION_REVEAL_OPTIONS.threshold,
        root: root ?? null,
        rootMargin: rootMargin ?? SECTION_REVEAL_OPTIONS.rootMargin,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold, root, rootMargin]);

  return [inView];
}
